import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { FilterBottomSheet, FilterBottomSheetOption } from "@/components/ui/filter-bottom-sheet";
import { useIsMobileFilterSheet } from "@/hooks/useIsMobileFilterSheet";

const DROPDOWN_MAX_HEIGHT_PX = 280;
const DROPDOWN_MIN_HEIGHT_PX = 120;
const DROPDOWN_GAP_PX = 4;

type FilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

function collectOptions(children: React.ReactNode, out: FilterOption[]): void {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      collectOptions((child.props as { children?: React.ReactNode }).children, out);
      return;
    }
    if (child.type !== "option") return;
    const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
    out.push({
      value: String(props.value ?? ""),
      label: String(props.children ?? ""),
      disabled: props.disabled,
    });
  });
}

function parseOptions(children: React.ReactNode): FilterOption[] {
  const options: FilterOption[] = [];
  collectOptions(children, options);
  return options;
}

function SelectionIndicator({ selected }: { selected: boolean }) {
  return (
    <span
      className={[
        "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
        selected ? "border-client-primary bg-client-primary" : "border-gray-300 bg-white",
      ].join(" ")}
      aria-hidden
    >
      {selected ? <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} /> : null}
    </span>
  );
}

export interface CategoryMultiFilterSelectProps {
  children: React.ReactNode;
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  allLabel?: string;
}

export function CategoryMultiFilterSelect({
  children,
  value,
  onChange,
  className = "",
  disabled,
  id,
  "aria-label": ariaLabel,
  allLabel = "Todos",
}: CategoryMultiFilterSelectProps) {
  const options = React.useMemo(() => parseOptions(children), [children]);
  const categoryOptions = React.useMemo(() => options.filter((o) => o.value !== ""), [options]);
  const todosOption = React.useMemo(() => options.find((o) => o.value === ""), [options]);

  const [open, setOpen] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const tagsScrollRef = React.useRef<HTMLDivElement>(null);
  const isMobileSheet = useIsMobileFilterSheet();
  const dragStateRef = React.useRef<{ active: boolean; startX: number; scrollLeft: number }>({
    active: false,
    startX: 0,
    scrollLeft: 0,
  });

  const listboxId = id ? `${id}-listbox` : undefined;
  const selectedSet = React.useMemo(() => new Set(value), [value]);
  const sheetTitle = (ariaLabel && String(ariaLabel).trim()) || "Categorias";

  const updatePanelPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP_PX;
    const spaceAbove = rect.top - DROPDOWN_GAP_PX;
    const openUpward = spaceBelow < DROPDOWN_MIN_HEIGHT_PX && spaceAbove > spaceBelow;
    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(
      DROPDOWN_MAX_HEIGHT_PX,
      Math.max(DROPDOWN_MIN_HEIGHT_PX, availableSpace - DROPDOWN_GAP_PX),
    );

    setPanelStyle({
      position: "fixed",
      top: openUpward ? Math.max(DROPDOWN_GAP_PX, rect.top - DROPDOWN_GAP_PX - maxHeight) : rect.bottom + DROPDOWN_GAP_PX,
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 250,
    });
  }, []);

  React.useEffect(() => {
    if (!open || isMobileSheet) return;
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, isMobileSheet, updatePanelPosition]);

  React.useEffect(() => {
    if (!open || isMobileSheet) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, isMobileSheet]);

  React.useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const drag = dragStateRef.current;
      if (!drag.active || !tagsScrollRef.current) return;
      e.preventDefault();
      const dx = e.pageX - drag.startX;
      tagsScrollRef.current.scrollLeft = drag.scrollLeft - dx;
    };
    const onMouseUp = () => {
      dragStateRef.current.active = false;
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const toggleCategory = (category: string) => {
    if (disabled) return;
    const opt = categoryOptions.find((o) => o.value === category);
    if (!opt || opt.disabled) return;
    if (selectedSet.has(category)) {
      onChange(value.filter((c) => c !== category));
    } else {
      onChange([...value, category]);
    }
  };

  const selectTodos = () => {
    if (disabled) return;
    onChange([]);
  };

  const removeCategory = (category: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    onChange(value.filter((c) => c !== category));
  };

  const handleTagsWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.shiftKey) return;
    e.preventDefault();
    e.currentTarget.scrollLeft += e.deltaY;
  };

  const handleTagsMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("[data-chip-remove]")) return;
    if (!tagsScrollRef.current) return;
    dragStateRef.current = {
      active: true,
      startX: e.pageX,
      scrollLeft: tagsScrollRef.current.scrollLeft,
    };
    e.preventDefault();
  };

  const triggerClassName = [
    "flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm",
    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const todosLabel = todosOption?.label ?? allLabel;
  const labelByValue = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of categoryOptions) map.set(opt.value, opt.label);
    return map;
  }, [categoryOptions]);

  const listContent = (
    <>
      {isMobileSheet ? (
        <FilterBottomSheetOption
          selected={value.length === 0}
          disabled={disabled}
          onSelect={selectTodos}
          leading={<SelectionIndicator selected={value.length === 0} />}
        >
          {todosLabel}
        </FilterBottomSheetOption>
      ) : (
        <button
          type="button"
          role="option"
          aria-selected={value.length === 0}
          className={[
            "flex w-full items-center gap-2 truncate whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors",
            value.length === 0 ? "bg-client-secondary font-medium text-gray-900" : "text-gray-900 hover:bg-gray-50",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={selectTodos}
        >
          <SelectionIndicator selected={value.length === 0} />
          <span className="min-w-0 flex-1 truncate">{todosLabel}</span>
        </button>
      )}
      {categoryOptions.map((opt) => {
        const isSelected = selectedSet.has(opt.value);
        if (isMobileSheet) {
          return (
            <FilterBottomSheetOption
              key={opt.value}
              selected={isSelected}
              disabled={opt.disabled}
              onSelect={() => toggleCategory(opt.value)}
              leading={<SelectionIndicator selected={isSelected} />}
            >
              {opt.label}
            </FilterBottomSheetOption>
          );
        }
        return (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={opt.disabled}
            className={[
              "flex w-full items-center gap-2 truncate whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors",
              isSelected ? "bg-client-secondary font-medium text-gray-900" : "text-gray-900 hover:bg-gray-50",
              opt.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => toggleCategory(opt.value)}
          >
            <SelectionIndicator selected={isSelected} />
            <span className="min-w-0 flex-1 truncate">{opt.label}</span>
          </button>
        );
      })}
    </>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        className={triggerClassName}
        onClick={() => {
          if (disabled) return;
          if (!open && !isMobileSheet) updatePanelPosition();
          setOpen((v) => !v);
        }}
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          {value.length === 0 ? (
            <span className="block truncate whitespace-nowrap text-left">{todosLabel}</span>
          ) : (
            <div
              ref={tagsScrollRef}
              className="flex max-h-6 flex-nowrap items-center gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onWheel={handleTagsWheel}
              onMouseDown={handleTagsMouseDown}
            >
              {value.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1.5 text-xs text-gray-900"
                >
                  <span className="max-w-[7rem] truncate whitespace-nowrap">{labelByValue.get(cat) ?? cat}</span>
                  <button
                    type="button"
                    data-chip-remove
                    className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                    aria-label={`Remover ${labelByValue.get(cat) ?? cat}`}
                    onClick={(e) => removeCategory(cat, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        )}
      </button>

      {isMobileSheet ? (
        <FilterBottomSheet
          open={open}
          title={sheetTitle}
          listboxId={listboxId}
          onClose={() => setOpen(false)}
        >
          {listContent}
        </FilterBottomSheet>
      ) : open && typeof document !== "undefined" ? (
        createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            aria-multiselectable
            style={panelStyle}
            className="overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white p-1.5 shadow-md"
            onWheel={(e) => e.stopPropagation()}
          >
            {listContent}
          </div>,
          document.body,
        )
      ) : null}
    </>
  );
}
